import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  ListItem,
  OrderedList,
  SimpleGrid,
  Text,
  useToken,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FC, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";

import { LoginOrBackButton } from "./components";
import { NAVIGATION_HEIGHT } from "./public-layout";

const SellingPoint: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  return (
    <ListItem rounded="large" bg="accentLight" px="12" py="10" display="block">
      <Heading color="primary" as="h6" pb={2} fontSize="xl">
        {title}
      </Heading>
      {children}
    </ListItem>
  );
};

const PAGE_PADDING = 10;

export const Landing: FC = () => {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const scrollRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const executeScroll = () => scrollRef.current && scrollRef.current.scrollIntoView();

  const [accent, accentLight] = useToken("colors", ["accent", "accentLight"]);

  return (
    <Box pb="10" minH="screen">
      <Flex
        bg={`linear-gradient(to top right, ${accent}, ${accentLight})`}
        pos="relative"
        h={`calc(100vh - ${NAVIGATION_HEIGHT})`}
        flexDir="column"
        justifyContent="center"
        p={PAGE_PADDING}
        overflow="hidden"
      >
        <Flex
          pointerEvents="none"
          ref={containerRef}
          alignItems="center"
          h="full"
          position="relative"
        >
          <Box pointerEvents="all" maxWidth="600px">
            <Heading
              ref={titleRef}
              as="h1"
              fontSize={{ base: "40px", md: "66px" }}
              mb={{ md: "5" }}
            >
              Dé online applicatie voor bronhouders in Nederland
            </Heading>
            <Flex gap={2}>
              <LoginOrBackButton />
              <Button bg="accentLight" onClick={executeScroll} variant="outlinePrimary">
                Lees meer
              </Button>
            </Flex>
          </Box>
        </Flex>
        <Box
          onClick={executeScroll}
          cursor="pointer"
          pos="absolute"
          bottom="10"
          left="50%"
          transform="translateX(-50%)"
        >
          <motion.div
            animate={{ y: 5 }}
            transition={{ repeat: Infinity, duration: 2, repeatType: "mirror" }}
          >
            <FaChevronDown size={20} />
          </motion.div>
        </Box>
      </Flex>
      <Box p={PAGE_PADDING} maxW="1500px" mx="auto">
        <Flex mt="20" flexDir={{ base: "column", md: "row" }} gap={PAGE_PADDING}>
          <Box w="full">
            <Heading ref={scrollRef} pb="5" as="h2">
              Eenvoudig jouw gronddata naar de BRO
            </Heading>
            <Text>
              BroStar is het platform dat bronhouders helpt om moeiteloos gegevens over putten,
              meetnetten en kwaliteitsgegevens aan te leveren aan de Basisregistratie Ondergrond
              (BRO). Geen gedoe meer met losse XML-bestanden of complexe software – Brostar biedt
              een overzichtelijke en gebruiksvriendelijke omgeving waarin je jouw data eenvoudig
              kunt beheren, aanpassen en indienen. Met Brostar sta je als organisatie direct in
              verbinding met de BRO – zonder technische barrières.
            </Text>
          </Box>
          <Image
            w={{ md: "50%" }}
            src="../../assets/images/convertion-explenation.png"
            alt="BROSTAR BRO grondwatermonitoring dashboard interface voor waterschappen"
            h="auto"
          />
        </Flex>
        <Heading pb="5" as="h2" pt="20">
          Waarom BroStar?
        </Heading>
        <OrderedList ml={0} px="0">
          <SimpleGrid gap={10} columns={{ base: 1, md: 2 }}>
            <SellingPoint title="Automatische XML-conversie">
              Laat de technische complexiteit aan ons over. Brostar zet jouw data automatisch om
              naar de juiste XML-formaten voor de BRO – geen handmatig werk meer nodig.
            </SellingPoint>

            <SellingPoint title="Zelf in controle">
              Beheer je gegevens wanneer jij dat wilt. Voeg eenvoudig nieuwe putten, meetnetten of
              kwaliteitsgegevens toe, of pas bestaande informatie aan – snel en veilig.
            </SellingPoint>

            <SellingPoint title="Bulk of enkele uploads">
              Werk zoals jij wilt: upload één dataset of honderden tegelijk. Brostar ondersteunt
              zowel individuele als bulkverwerkingen zonder extra moeite.
            </SellingPoint>

            <SellingPoint title="Directe feedback">
              Na het aanleveren van je data ontvang je meteen een reactie – zo weet je direct of
              alles goed is gegaan, en wat er eventueel nog moet worden aangepast.
            </SellingPoint>

            <SellingPoint title="Visuele bediening">
              Geen ondoorgrondelijke formulieren of code. Met Brostar werk je in een intuïtieve,
              visuele interface die speciaal is ontworpen voor bronhouders.
            </SellingPoint>
          </SimpleGrid>
        </OrderedList>

        {/* FAQ Section */}
        <Heading pb="5" as="h2" pt="20" id="faq">
          Overzicht BRO-berichten en bewerkingsopties
        </Heading>

        <Heading as="h3" size="md" pb="3" pt="5">
          Database en Gegevensopslag
        </Heading>

        <Box mb="6" p="5" border="1px solid #e2e8f0" borderRadius="md">
          <Heading as="h4" size="sm" pb="2" color="teal.600">
            Werkt BROSTAR als database voor mijn grondwatergegevens?
          </Heading>
          <Text maxW="1000px" mb="3">
            <strong>Nee, BROSTAR is geen database voor opslag van brongegevens.</strong> BROSTAR
            fungeert als een doorgeefluik tussen uw systemen en de Basis Registratie Ondergrond
            (BRO). De software:
          </Text>
          <OrderedList pl="4" mb="3">
            <ListItem>Levert nieuwe grondwatermonitoringgegevens aan de BRO</ListItem>
            <ListItem>Haalt bestaande gegevens op uit de BRO voor wijzigingen</ListItem>
            <ListItem>Zorgt voor correcte formatting volgens BRO-standaarden</ListItem>
            <ListItem>Valideert gegevens voordat ze naar de BRO worden verstuurd</ListItem>
          </OrderedList>
          <Text>
            Voor permanente opslag van uw brongegevens heeft u een eigen database of
            gegevensmanagementsysteem nodig.
          </Text>
        </Box>

        <Box mb="6" p="5" border="1px solid #e2e8f0" borderRadius="md">
          <Heading as="h4" size="sm" pb="2" color="teal.600">
            Kan ik BROSTAR gebruiken als ik al een eigen database heb?
          </Heading>
          <Text mb="3">
            <strong>
              Ja, BROSTAR is juist ontworpen om samen te werken met bestaande systemen.
            </strong>{" "}
            U heeft twee opties:
          </Text>

          <Heading as="h5" size="xs" pb="2" pt="2">
            Automatische koppeling via API
          </Heading>
          <Text mb="2">
            Voor grote organisaties met veel gegevens bieden we <strong>API-koppelingen</strong> aan
            die:
          </Text>
          <OrderedList pl="4" mb="3">
            <ListItem>Automatisch gegevens uit uw database ophalen</ListItem>
            <ListItem>Deze omzetten naar BRO-formaat</ListItem>
            <ListItem>Direct doorsturen naar de Basis Registratie Ondergrond</ListItem>
            <ListItem>Foutmeldingen terugkoppelen naar uw systeem</ListItem>
          </OrderedList>

          <Heading as="h5" size="xs" pb="2" pt="2">
            Handmatige invoer via formulieren
          </Heading>
          <Text mb="2">
            Voor kleinere bronhouders is handmatige invoer vaak <strong>kosteneffectiever</strong>:
          </Text>
          <OrderedList pl="4">
            <ListItem>Gebruiksvriendelijke webformulieren</ListItem>
            <ListItem>Stapsgewijze validatie van gegevens</ListItem>
            <ListItem>Directe feedback bij fouten</ListItem>
            <ListItem>Geen investeringen in systeemkoppelingen</ListItem>
          </OrderedList>
        </Box>

        <Box mb="6" p="5" border="1px solid #e2e8f0" borderRadius="md">
          <Heading as="h4" size="sm" pb="2" color="teal.600">
            Welke grondwatermonitoring gegevens kan ik via BROSTAR aanleveren?
          </Heading>
          <Text mb="3">BROSTAR ondersteunt alle BRO-gegevenstypen voor grondwatermonitoring:</Text>
          <OrderedList pl="4" mb="3">
            <ListItem>
              <strong>Grondwatermonitoringput (GMW)</strong> - Putgegevens en onderhoudsinformatie
            </ListItem>
            <ListItem>
              <strong>Grondwatermonitoringnet (GMN)</strong> - Meetnetinformatie en meetpunten
            </ListItem>
            <ListItem>
              <strong>Grondwaterstandsdossiers (GLD)</strong> - Tijdreeksen van grondwaterstanden
            </ListItem>
            <ListItem>
              <strong>Grondwaterkwaliteitsonderzoek (GAR)</strong> - Chemische analyses en
              kwaliteitsparameters
            </ListItem>
            <ListItem>
              <strong>Formatieweerstandsdossiers (FRD)</strong> - Zoutwachter metingen
            </ListItem>
          </OrderedList>
          <Text>
            Alle gegevens worden automatisch gevalideerd volgens de officiële BRO-catalogus voordat
            ze worden aangeleverd.
          </Text>
        </Box>

        <Flex flexDir={{ base: "column", md: "row" }} gap={PAGE_PADDING}>
          <Box>
            <Heading pb="5" as="h2" pt="10">
              Start vandaag nog met BROSTAR
            </Heading>
            <Text mb="5" maxW="800px">
              Ben je al gebruiker? Log dan direct in om gebruik te maken van alle functionaliteiten
              voor jouw organisatie.
            </Text>
            <LoginOrBackButton />
          </Box>
          <Box>
            <Heading pb="5" as="h2" pt="10">
              Wil je meer weten?
            </Heading>
            <Text mb="5" maxW="1000px">
              Voor alle technische zaken kan je terecht op de{" "}
              <Link href="https://docs.brostar.nl" color="primary" isExternal>
                documentatie
              </Link>
              . Neem{" "}
              <Link href="mailto:secretariaat@nelen-schuurmans.nl" color="primary">
                contact
              </Link>{" "}
              met ons op voor meer informatie over hoe brostar.nl jouw data management kan
              verbeteren. Onze experts staan klaar om je te helpen en al je vragen te beantwoorden.
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};
